import React from "react";
import { defineMessages, FormattedDate, FormattedMessage } from "react-intl";

import { Card, CardContent } from "src/common/components/Card";
import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { useOrgModParams } from "src/features/orgmod";
import { modMessages, orgMessages } from "src/messages/generic";
import {
	GroupContentItem,
	GroupDescriptor,
	GroupedListDescriptor,
	GroupListDescriptor,
	ListDescriptor,
	ListItemDescriptor,
	RecordDescriptor,
	SectionContentItem,
	SectionDescriptor,
	TableDescriptor,
	TableGroupListDescriptor
} from "src/modules/shared/dashboard/types";

import "./Report.css";

const messages = defineMessages({
	patientDataReport: {
		id: "dashboard.patient_data_report",
		defaultMessage: "Páciens adat riport"
	}
});

export const Report: React.FunctionComponent<{
	header?: { label?: string; value?: string }[];
	sections: SectionDescriptor[];
}> = ({ header, sections }) => {
	const formatMessage = useFormatMessageMap();
	const { org, mod } = useOrgModParams();
	return (
		<Card>
			<CardContent>
				<div className="report">
					<div className="report__header">
						<h1 className="report__title">
							<FormattedMessage {...messages.patientDataReport} />
							{" - "}
							<FormattedDate
								value={new Date()}
								year="numeric"
								month="numeric"
								day="numeric"
								hour="numeric"
								minute="numeric"
								second="numeric"
							/>
						</h1>
						<h2 className="report__sub-title">
							{formatMessage(orgMessages, org)}
							{" - "}
							{formatMessage(modMessages, mod)}
						</h2>
					</div>
					{header && (
						<div className="report__section">
							<div className="report__section-content">
								{header.map((item, i) => (
									<RecordRenderer
										key={i}
										variant="short"
										{...item}
									/>
								))}
							</div>
						</div>
					)}
					{sections
						.filter(isNotTimelineOnlySection)
						.map((section, i) => (
							<SectionRenderer key={i} {...section} />
						))}
				</div>
			</CardContent>
		</Card>
	);
};

function isNotTimelineOnlySection(section: SectionDescriptor): boolean {
	if (
		section.content.length === 1 &&
		section.content[0].type === "timeline"
	) {
		return false;
	}
	return true;
}

const SectionRenderer: React.FunctionComponent<SectionDescriptor> = ({
	label,
	content
}) =>
	content.length === 0 ? null : (
		<div className="report__section">
			<h2>{label}</h2>
			<div className="report__section-content">
				{content.map((item, i) => (
					<SectionContentItemRenderer key={i} {...item} />
				))}
			</div>
		</div>
	);

const SectionContentItemRenderer: React.FunctionComponent<
	SectionContentItem
> = item => {
	if (item.type === "group-list") {
		return <GroupListRenderer {...item} />;
	}
	if (item.type === "group") {
		return <GroupRenderer {...item} />;
	}
	if (item.type === "table") {
		return <TableRenderer {...item} />;
	}
	if (item.type === "table-group-list") {
		return <TableGroupListRenderer {...item} />;
	}
	if (item.type === "grouped-list") {
		return <GroupedListRenderer {...item} />;
	}
	if (item.type === "list") {
		return <ListRenderer {...item} />;
	}
	if (item.type === "record") {
		return <RecordRenderer {...item} />;
	}
	return null;
};

const GroupListRenderer: React.FunctionComponent<GroupListDescriptor> = ({
	groups
}) =>
	groups.length === 0 ? null : (
		<div className="report__group-list">
			{groups.map((group, i) => (
				<GroupRenderer key={i} {...group} />
			))}
		</div>
	);

const GroupRenderer: React.FunctionComponent<GroupDescriptor> = ({
	label,
	content
}) =>
	content.length === 0 ? null : (
		<div className="report__group">
			<h3>{label}</h3>
			<div className="report__group-content">
				{content.map((item, i) => (
					<GroupContentItemRenderer key={i} {...item} />
				))}
			</div>
		</div>
	);

const GroupContentItemRenderer: React.FunctionComponent<
	GroupContentItem
> = item => {
	if (item.type === "table") {
		return <TableRenderer {...item} />;
	}
	if (item.type === "table-group-list") {
		return <TableGroupListRenderer {...item} />;
	}
	if (item.type === "grouped-list") {
		return <GroupedListRenderer {...item} />;
	}
	if (item.type === "list") {
		return <ListRenderer {...item} />;
	}
	if (item.type === "record") {
		return <RecordRenderer {...item} />;
	}
	return null;
};

const TableRenderer: React.FunctionComponent<TableDescriptor> = ({
	label,
	columns,
	rows
}) => (
	<div className="report__table">
		<h4>{label}</h4>
		<div className="report__table-content">
			<table>
				<thead>
					<tr>
						{columns.map((label, i) => (
							<th key={i}>{label}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((values, i1) => (
						<tr key={i1}>
							{values.map((value, i2) => (
								<td key={i2}>{value}</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	</div>
);

const TableGroupListRenderer: React.FunctionComponent<
	TableGroupListDescriptor
> = ({ label, columns, groups }) => (
	<div className="report__table">
		<h4>{label}</h4>
		<div className="report__table-content">
			<table>
				<thead>
					<tr>
						{columns.map((label, i) => (
							<th key={i}>{label}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{groups.map(({ label, rows }, i1) => (
						<React.Fragment key={i1}>
							{label && (
								<tr>
									<td
										colSpan={
											rows.length === 0
												? 0
												: rows[0].length
										}
									>
										<b>{label}</b>
									</td>
								</tr>
							)}
							{rows.map((values, i2) => (
								<tr key={i2}>
									{values.map((value, i3) => (
										<td key={i3}>{value}</td>
									))}
								</tr>
							))}
						</React.Fragment>
					))}
				</tbody>
			</table>
		</div>
	</div>
);

const GroupedListRenderer: React.FunctionComponent<GroupedListDescriptor> = ({
	groups
}) => (
	<div className="report__grouped-list">
		{groups.map(({ label, items }, i) => (
			<div className="report__list" key={i}>
				<h4>{label}</h4>
				<div className="report__list-content">
					{items.map((item, i) => (
						<ListItemRenderer key={i} {...item} />
					))}
				</div>
			</div>
		))}
	</div>
);

const ListRenderer: React.FunctionComponent<ListDescriptor> = ({
	label,
	items
}) => (
	<div className="report__list">
		<h4>{label}</h4>
		<div className="report__list-content">
			{items.map((item, i) => (
				<ListItemRenderer key={i} {...item} />
			))}
		</div>
	</div>
);

const ListItemRenderer: React.FunctionComponent<ListItemDescriptor> = ({
	label,
	value
}) => (
	<div className="report__list-item">
		{label && (
			<b>
				{label}
				{value && ": "}
			</b>
		)}
		{value}
	</div>
);

const RecordRenderer: React.FunctionComponent<
	Pick<RecordDescriptor, "variant" | "label" | "value">
> = ({ variant = "short", label, value }) => (
	<div className={`report__record report__record--${variant}`}>
		{label &&
			(value ? (
				<b>
					{label}
					{": "}
				</b>
			) : (
				label
			))}
		{value}
	</div>
);
